import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { speakingContent } from "@/brand/content";
import { SiteHeader5 } from "@/components/layout/SiteHeader5";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InteriorHero } from "@/components/common/InteriorHero";
import { ConsultationBanner } from "@/components/common/ConsultationBanner";

/**
 * SpeakingPage — Speaking & Learning Spaces template.
 *
 * Route: /concept-e/speaking
 */
export default function SpeakingPage() {
  return (
    <main
      data-testid="page-speaking"
      className="min-h-screen"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <SiteHeader5 />

      <InteriorHero
        breadcrumb={[
          { label: "Faith 1st", href: "/concept-e" },
          { label: "Speaking" },
        ]}
        eyebrow={speakingContent.eyebrow}
        title={speakingContent.headline}
        intro={speakingContent.intro}
        meta={[
          ["Topics", String(speakingContent.topics.length).padStart(2, "0")],
          ["Format", "Keynote · Workshop · Facilitation"],
        ]}
      />

      {/* Topics list */}
      <section
        data-testid="speaking-topics"
        style={{ background: C.surfaceLight2 }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-7">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Topics
                </p>
              </div>
              <h2
                className="font-cormorant"
                style={{
                  color: C.textOnLight,
                  fontSize: "clamp(2rem, 3.6vw, 3rem)",
                  fontWeight: 400,
                  lineHeight: 1.06,
                  letterSpacing: "-0.012em",
                }}
              >
                Content built for
                <br />
                your board, leadership team,
                <br />
                or association.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
              <p
                className="font-inter leading-[1.7] max-w-[42ch]"
                style={{ color: C.inkOnLight, fontSize: "1rem" }}
              >
                Every engagement is designed for the specific room. No
                boilerplate keynote, no generic curriculum. Recent topics
                include the following — and custom sessions are welcomed.
              </p>
            </div>
          </div>

          <ul
            className="border-t"
            style={{ borderColor: C.borderSoft }}
            data-testid="topics-list"
          >
            {speakingContent.topics.map((t, i) => (
              <li
                key={t.title}
                data-testid={`speaking-topic-${i}`}
                className="grid grid-cols-12 gap-x-6 gap-y-3 py-10 lg:py-12"
                style={{ borderBottom: `1px solid ${C.borderSoft}` }}
              >
                <div className="col-span-2 lg:col-span-1">
                  <span
                    className="font-cormorant tabular-nums"
                    style={{
                      color: C.accentMetal,
                      fontSize: "clamp(1.6rem, 2vw, 1.9rem)",
                      lineHeight: 1,
                    }}
                  >
                    {t.n}
                  </span>
                </div>
                <div className="col-span-10 lg:col-span-5">
                  <h3
                    className="font-cormorant"
                    style={{
                      color: C.textOnLight,
                      fontSize: "clamp(1.4rem, 1.8vw, 1.7rem)",
                      fontWeight: 400,
                      lineHeight: 1.15,
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t.title}
                  </h3>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <p
                    className="font-inter leading-[1.65]"
                    style={{ color: C.inkOnLight, fontSize: "0.98rem" }}
                  >
                    {t.body}
                  </p>
                  <p
                    className="mt-4 font-inter text-[10.5px] uppercase font-semibold"
                    style={{
                      color: C.accentFunctional,
                      letterSpacing: "0.22em",
                    }}
                  >
                    {t.format}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Past engagements */}
      <section
        data-testid="speaking-engagements"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-4 mb-7">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Recent engagements
                </p>
              </div>
              <h3
                className="font-cormorant"
                style={{
                  color: C.textOnLight,
                  fontSize: "clamp(1.6rem, 2.4vw, 2.2rem)",
                  fontWeight: 400,
                  lineHeight: 1.12,
                }}
              >
                A partial list of rooms
                <br />
                Faith 1st has been in.
              </h3>
            </div>
            <div className="lg:col-span-8 lg:pl-6">
              <ul>
                {speakingContent.engagements.map((e, i) => (
                  <li
                    key={e}
                    data-testid={`engagement-${i}`}
                    className="py-5 flex items-baseline gap-6"
                    style={{
                      borderTop:
                        i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span
                      className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p
                      className="font-inter"
                      style={{
                        color: C.inkOnLight,
                        fontSize: "0.98rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {e}
                    </p>
                  </li>
                ))}
              </ul>

              <a
                href="#"
                data-testid="speaking-testimonials-link"
                className="group mt-10 inline-flex items-center gap-2 pb-1 font-inter text-[11px] uppercase font-semibold border-b transition-colors"
                style={{
                  color: C.accentMetal,
                  borderColor: C.accentMetal,
                  letterSpacing: "0.26em",
                }}
              >
                Read what audiences said
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ConsultationBanner
        eyebrow="Booking"
        title="Bring Faith 1st into your room."
        body="Speaking inquiries are handled by Katrina directly. Share the shape of the moment and we'll respond within two business days."
        ctaLabel="Inquire about speaking"
      />
      <SiteFooter
        stamp="Concept 05"
        labelSuffix="Speaking"
        markSubtitle="Leadership, People & Culture"
        descriptor="Faith 1st is a Leadership, People & Culture Consulting firm helping mission-driven and values-led organizations navigate high-stakes people moments through consulting, training, coaching, and facilitation."
      />
    </main>
  );
}
